import { useState } from 'react';

interface DeliverableItem {
  id: string;
  title: string;
  icon: string;
  description: string;
  features: string[];
  mockPreview: {
    type: 'list' | 'table' | 'document';
    items?: string[];
    columns?: string[];
    rows?: string[][];
  };
}

const deliverables: DeliverableItem[] = [
  {
    id: 'missing-materials',
    title: '缺资料清单',
    icon: 'ri-file-list-2-line',
    description: '按批次/票号展示缺失的资料项，包括报关单、发票、物流单据等，支持批量补充上传。',
    features: [
      '按批次/票号自动分组展示',
      '标红缺失项，优先级排序',
      '支持批量上传补充',
      '实时更新完整度进度',
    ],
    mockPreview: {
      type: 'list',
      items: [
        '批次 2024-001：缺报关单、物流单据（2/5 已完成）',
        '批次 2024-002：缺发票、收款凭证（1/4 已完成）',
        '票号 HK20240315：缺装箱单（3/4 已完成）',
      ],
    },
  },
  {
    id: 'validation-report',
    title: '校验报告',
    icon: 'ri-shield-check-line',
    description: '提交前自动校验数据完整性、格式规范性、逻辑一致性，标红异常项并提供修正建议。',
    features: [
      '数据完整性校验（必填项检查）',
      '格式规范性校验（HS编码/金额格式）',
      '逻辑一致性校验（三流对齐）',
      '异常项标红 + 修正建议',
    ],
    mockPreview: {
      type: 'table',
      columns: ['校验项', '状态', '异常说明'],
      rows: [
        ['报关单号格式', '✓ 通过', '-'],
        ['发票金额一致性', '✗ 异常', '发票金额与报关金额差异 ¥120'],
        ['收款凭证完整性', '✓ 通过', '-'],
        ['HS编码规范性', '⚠ 警告', '建议补充商品详细描述'],
      ],
    },
  },
  {
    id: 'discrepancy-record',
    title: '三流差异留痕',
    icon: 'ri-git-branch-line',
    description: '自动比对资金流、物流、信息流，标记差异项并记录调整过程，确保可追溯。',
    features: [
      '三流自动比对（资金/物流/信息）',
      '差异项自动标红',
      '调整过程完整记录',
      '差异原因可备注说明',
    ],
    mockPreview: {
      type: 'table',
      columns: ['对比维度', '差异金额', '调整状态'],
      rows: [
        ['报关金额 vs 发票金额', '¥0', '一致'],
        ['收款金额 vs 报关金额', '-¥50', '已调整（汇率差异）'],
        ['物流重量 vs 报关重量', '+2kg', '已说明（包装差异）'],
      ],
    },
  },
  {
    id: 'archive-directory',
    title: '归档目录',
    icon: 'ri-folder-zip-line',
    description: '按票号/批次自动生成归档目录，支持在线预览、批量下载、长期保存。',
    features: [
      '按票号/批次自动归档',
      '目录结构清晰可导出',
      '支持在线预览与下载',
      '服务期内长期保存',
    ],
    mockPreview: {
      type: 'document',
      items: [
        '📁 2024-Q1 归档包',
        '  📄 报关单 (15份)',
        '  📄 发票 (15份)',
        '  📄 物流单据 (12份)',
        '  📄 收款凭证 (15份)',
        '  📄 核销链路记录 (15份)',
      ],
    },
  },
];

export default function DeliverablesShowcase() {
  const [activeTab, setActiveTab] = useState(0);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set(['missing-materials']));

  const toggleExpand = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const activeDeliverable = deliverables[activeTab];

  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">交付物与留痕示例</h2>
          <p className="text-lg text-gray-600">
            从数据归集到证据链固化，每个环节都有清晰的交付物与可追溯记录
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center gap-3 mb-12 flex-wrap">
          {deliverables.map((item, index) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(index)}
              className={`flex items-center gap-2 px-6 h-12 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === index
                  ? 'bg-gradient-to-r from-[#14B8A6] to-[#0D9488] text-white shadow-lg'
                  : 'bg-white text-gray-600 border-2 border-gray-200 hover:border-[#14B8A6]'
              }`}
            >
              <i className={`${item.icon} text-lg`}></i>
              {item.title}
            </button>
          ))}
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-[#14B8A6]/10 to-[#0D9488]/10 px-8 py-6 border-b border-gray-200">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-[#14B8A6] to-[#0D9488] rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className={`${activeDeliverable.icon} text-2xl text-white`}></i>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">{activeDeliverable.title}</h3>
                <p className="text-gray-600 leading-relaxed">{activeDeliverable.description}</p>
              </div>
            </div>
          </div>

          {/* Card Body */}
          <div className="p-8">
            {/* Features Grid */}
            <div className="mb-8">
              <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">核心能力</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {activeDeliverable.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#14B8A6]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[#14B8A6] text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Mock Preview */}
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-eye-line text-[#14B8A6] text-lg"></i>
                <span className="text-sm font-bold text-gray-700">示例预览</span>
              </div>

              {/* List Preview */}
              {activeDeliverable.mockPreview.type === 'list' && (
                <div className="space-y-3">
                  {activeDeliverable.mockPreview.items?.map((item, index) => (
                    <div key={index} className="bg-white rounded-lg px-4 py-3 border border-gray-200 flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#FF6B35] rounded-full flex-shrink-0"></div>
                      <span className="text-sm text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Table Preview */}
              {activeDeliverable.mockPreview.type === 'table' && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        {activeDeliverable.mockPreview.columns?.map((col, index) => (
                          <th key={index} className="text-left py-3 px-4 font-bold text-gray-700 bg-white">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {activeDeliverable.mockPreview.rows?.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-b border-gray-100 last:border-0">
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex} className="py-3 px-4 text-gray-700 bg-white">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Document Preview */}
              {activeDeliverable.mockPreview.type === 'document' && (
                <div className="bg-white rounded-lg p-4 border border-gray-200 font-mono text-sm">
                  {activeDeliverable.mockPreview.items?.map((item, index) => (
                    <div key={index} className="text-gray-700 leading-relaxed">
                      {item}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Accordion Alternative (Optional) */}
        <div className="mt-12">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="px-8 py-6 bg-gray-50 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">全部交付物（可展开查看）</h3>
            </div>
            <div className="divide-y divide-gray-200">
              {deliverables.map((item) => {
                const isExpanded = expandedItems.has(item.id);
                return (
                  <div key={item.id}>
                    {/* Accordion Header */}
                    <button
                      onClick={() => toggleExpand(item.id)}
                      className="w-full px-8 py-5 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#14B8A6] to-[#0D9488] rounded-lg flex items-center justify-center">
                          <i className={`${item.icon} text-lg text-white`}></i>
                        </div>
                        <div className="text-left">
                          <h4 className="font-bold text-gray-800">{item.title}</h4>
                          <p className="text-sm text-gray-500">{item.description}</p>
                        </div>
                      </div>
                      <i
                        className={`ri-arrow-down-s-line text-2xl text-gray-400 transition-transform ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>

                    {/* Accordion Content */}
                    {isExpanded && (
                      <div className="px-8 py-6 bg-gray-50 border-t border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                          {item.features.map((feature, index) => (
                            <div key={index} className="flex items-start gap-2">
                              <i className="ri-check-line text-[#14B8A6] text-sm mt-1"></i>
                              <span className="text-sm text-gray-700">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}